import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Send, Loader2, Undo2 } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';
import { videoStudioService } from '../../services/videoStudioService';
import {
  VideoClip, AudioClip, TextOverlayClip, ImageOverlayClip, CaptionTrack
} from '../../types/videoStudio';

type AspectRatio = '16:9' | '9:16' | '1:1' | '4:3';
type OverlayClip = TextOverlayClip | ImageOverlayClip;

interface TimelineState {
  videoClips: VideoClip[];
  audioClips: AudioClip[];
  overlayClips: OverlayClip[];
  captionTracks: CaptionTrack[];
  aspectRatio: AspectRatio;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  timeline: TimelineState;
  onApply: (next: TimelineState) => void;
}

const SUGGESTION_KEYS = ['suggestion1', 'suggestion2', 'suggestion3', 'suggestion4'] as const;

const AIEditModal: React.FC<Props> = ({ isOpen, onClose, timeline, onApply }) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [snapshot, setSnapshot] = useState<TimelineState | null>(null);
  const [lastSummary, setLastSummary] = useState<string | null>(null);
  const [lastTools, setLastTools] = useState<Array<{ name: string; ok: boolean; error?: string }>>([]);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 80);
    // O snapshot também tem que morrer ao fechar: guardado, o botão "Desfazer"
    // reaparecia depois e restaurava a timeline de ANTES da edição de IA antiga,
    // apagando tudo que o usuário fez manualmente no intervalo.
    else { setPrompt(''); setLastSummary(null); setLastTools([]); setSnapshot(null); }
  }, [isOpen]);

  const submit = async () => {
    const text = prompt.trim();
    if (!text || loading) return;

    setLoading(true);
    setSnapshot(timeline);
    setLastSummary(null);
    setLastTools([]);

    try {
      const res = await videoStudioService.aiEdit({ prompt: text, timeline });
      if (res.success && res.data) {
        onApply(res.data.timeline as TimelineState);
        const ok = (res.data.toolCalls || []).filter(tc => tc.ok).length;
        setLastSummary(res.data.summary || (t('videoStudio.editor.aiEdit.appliedOther', { count: 0 }) as string));
        setLastTools((res.data.toolCalls || []).map(tc => ({ name: tc.name, ok: tc.ok, error: tc.error })));
        if (ok === 0) {
          toast(t('videoStudio.editor.aiEdit.noChange') as string, { icon: 'ℹ️' });
        } else {
          const key = ok === 1 ? 'videoStudio.editor.aiEdit.appliedOne' : 'videoStudio.editor.aiEdit.appliedOther';
          toast.success(t(key, { count: ok }) as string);
          setPrompt('');
        }
      } else {
        const msg = (res as any).message || (t('videoStudio.editor.aiEdit.failed') as string);
        toast.error(msg);
      }
    } catch (err: any) {
      const code = err.response?.data?.code;
      const msg = err.response?.data?.message || err.message || (t('videoStudio.editor.aiEdit.failed') as string);
      if (code === 'INSUFFICIENT_VIDEO_CREDITS') toast.error(t('videoStudio.editor.aiEdit.insufficientCredits') as string);
      else toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const undoLast = () => {
    if (!snapshot) return;
    onApply(snapshot);
    setSnapshot(null);
    setLastSummary(null);
    setLastTools([]);
    toast.success(t('videoStudio.editor.aiEdit.undoToast') as string);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('videoStudio.editor.aiEdit.title') as string} size="md">
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <Sparkles className="w-3.5 h-3.5 text-fuchsia-500" />
          <span>{t('videoStudio.editor.aiEdit.description')}</span>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
            {t('videoStudio.editor.aiEdit.instructionLabel')}
          </label>
          <textarea
            ref={inputRef}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('videoStudio.editor.aiEdit.placeholder') as string}
            rows={3}
            disabled={loading}
            className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white text-sm resize-none focus:outline-none focus:border-fuchsia-500/50 disabled:opacity-50"
          />
          <p className="mt-1 text-[10px] text-gray-500">{t('videoStudio.editor.aiEdit.shortcut')}</p>
        </div>

        <div>
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{t('videoStudio.editor.aiEdit.suggestionsLabel')}</p>
          <div className="space-y-1">
            {SUGGESTION_KEYS.map(key => {
              const s = t(`videoStudio.editor.aiEdit.${key}`) as string;
              return (
                <button key={key} type="button" onClick={() => setPrompt(s)} disabled={loading}
                  className="w-full text-left px-3 py-1.5 rounded-lg text-xs bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-fuchsia-500/50 transition disabled:opacity-50">
                  {s}
                </button>
              );
            })}
          </div>
        </div>

        {lastSummary && (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
            <p className="text-xs text-emerald-800 dark:text-emerald-300 font-medium">{lastSummary}</p>
            {lastTools.length > 0 && (
              <div className="mt-2 space-y-0.5">
                {lastTools.map((tc, i) => (
                  <p key={i} className={`text-[10px] font-mono ${tc.ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                    {tc.ok ? '✓' : '✗'} {tc.name}{tc.error ? ` — ${tc.error}` : ''}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <div>
            {snapshot && (
              <button onClick={undoLast} type="button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition">
                <Undo2 className="w-3.5 h-3.5" /> {t('videoStudio.editor.aiEdit.undo')}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} type="button"
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
              {t('videoStudio.editor.aiEdit.close')}
            </button>
            <button onClick={submit} disabled={loading || !prompt.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white disabled:opacity-50 hover:brightness-110 transition">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {loading ? t('videoStudio.editor.aiEdit.sending') : t('videoStudio.editor.aiEdit.send')}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AIEditModal;
